import { useState, useEffect, useRef } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { collection, query, where, getDocs, getDoc, setDoc, updateDoc, doc, arrayUnion } from "firebase/firestore";
import { auth, provider, db } from "./firebase";
import { warmCache } from "./proactiveCache";
import { reportLastSeen } from "./lastSeen";
import TripsList from "./components/TripsList";
import TripDetail from "./components/TripDetail";
import StatsPage from "./components/StatsPage";
import TripItImport from "./components/TripItImport";
import RestaurantsPage from "./components/RestaurantsPage";
import SettingsPage from "./components/SettingsPage";
import PackingListsPage from "./components/PackingListsPage";
import OnboardingCards from "./components/OnboardingCards";
import "./App.css";

// ---- Gulliver SVG scenes ----

const GulliverOnPlane = () => (
  <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g transform="scale(-1,1) translate(-60,0)">
      <ellipse cx="30" cy="36" rx="18" ry="6" fill="#3B6ECC"/>
      <path d="M48 36 Q54 36 56 34 Q54 38 48 38 Z" fill="#3B6ECC"/>
      <path d="M14 36 Q8 32 10 28 Q13 33 14 35 Z" fill="#3B6ECC"/>
      <path d="M22 36 Q18 28 6 24 Q10 31 22 34 Z" fill="#2C5AAF"/>
      <path d="M22 38 Q18 46 6 50 Q10 43 22 40 Z" fill="#2C5AAF"/>
      <ellipse cx="40" cy="35" rx="4" ry="3" fill="#87CEEB" opacity="0.9"/>
      <ellipse cx="46" cy="35" rx="3" ry="3" fill="#87CEEB" opacity="0.9"/>
    </g>
    <ellipse cx="30" cy="28" rx="5" ry="6" fill="#FAFAF8"/>
    <circle cx="30" cy="20" r="6" fill="#F5D5A0"/>
    <circle cx="28" cy="19" r="1" fill="#1A1A1A"/>
    <circle cx="32" cy="19" r="1" fill="#1A1A1A"/>
    <path d="M28 22 Q30 24 32 22" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="30" cy="15" rx="7" ry="2" fill="#C05621"/>
    <rect x="27" y="9" width="6" height="7" rx="1" fill="#C05621"/>
    <rect x="27" y="13" width="6" height="2" fill="#E8B84B"/>
    <path d="M25 27 Q20 24 16 22" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M35 27 Q40 24 44 22" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <circle cx="15" cy="21" r="2" fill="#F5D5A0"/>
    <circle cx="45" cy="21" r="2" fill="#F5D5A0"/>
    <path d="M27 33 Q24 38 22 42" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M33 33 Q36 38 38 42" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <ellipse cx="21" cy="43" rx="3" ry="2" fill="#C05621"/>
    <ellipse cx="39" cy="43" rx="3" ry="2" fill="#C05621"/>
  </svg>
);

const GulliverOnTrain = () => (
  <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Train body */}
    <rect x="5" y="32" width="55" height="20" rx="4" fill="#2C5AAF"/>
    <rect x="5" y="28" width="20" height="8" rx="3" fill="#3B6ECC"/>
    {/* Chimney + steam */}
    <rect x="10" y="20" width="5" height="10" rx="1" fill="#1A1A1A"/>
    <ellipse cx="12" cy="18" rx="4" ry="3" fill="#CCCCCC" opacity="0.6"/>
    <ellipse cx="14" cy="13" rx="3" ry="2" fill="#CCCCCC" opacity="0.4"/>
    <ellipse cx="11" cy="9" rx="2" ry="2" fill="#CCCCCC" opacity="0.2"/>
    {/* Windows */}
    <rect x="28" y="35" width="10" height="8" rx="2" fill="#87CEEB"/>
    <rect x="42" y="35" width="10" height="8" rx="2" fill="#87CEEB"/>
    {/* Wheels */}
    <circle cx="18" cy="52" r="5" fill="#1A1A1A"/>
    <circle cx="18" cy="52" r="2" fill="#888"/>
    <circle cx="38" cy="52" r="5" fill="#1A1A1A"/>
    <circle cx="38" cy="52" r="2" fill="#888"/>
    <circle cx="54" cy="52" r="4" fill="#1A1A1A"/>
    <circle cx="54" cy="52" r="1.5" fill="#888"/>
    {/* Track */}
    <line x1="0" y1="57" x2="80" y2="57" stroke="#888" strokeWidth="2"/>
    {/* Gulliver sitting on top waving */}
    <ellipse cx="50" cy="26" rx="4" ry="5" fill="#FAFAF8"/>
    <circle cx="50" cy="18" r="5" fill="#F5D5A0"/>
    <circle cx="48" cy="17" r="1" fill="#1A1A1A"/>
    <circle cx="52" cy="17" r="1" fill="#1A1A1A"/>
    <path d="M48 20 Q50 22 52 20" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="50" cy="13" rx="6" ry="2" fill="#C05621"/>
    <rect x="47" y="8" width="6" height="6" rx="1" fill="#C05621"/>
    <rect x="47" y="11" width="6" height="2" fill="#E8B84B"/>
    {/* Waving arm */}
    <path d="M54 24 Q60 18 62 14" stroke="#F5D5A0" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <circle cx="63" cy="13" r="2" fill="#F5D5A0"/>
    <path d="M46 24 Q42 26 40 28" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
);

const GulliverOnDonkey = () => (
  <svg viewBox="0 0 70 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Donkey body */}
    <ellipse cx="35" cy="42" rx="20" ry="10" fill="#A0896B"/>
    {/* Donkey head */}
    <ellipse cx="52" cy="36" rx="8" ry="6" fill="#A0896B"/>
    {/* Donkey ears */}
    <ellipse cx="50" cy="29" rx="2" ry="5" fill="#A0896B"/>
    <ellipse cx="50" cy="29" rx="1" ry="3" fill="#E8C4B8"/>
    <ellipse cx="56" cy="30" rx="2" ry="4" fill="#A0896B"/>
    <ellipse cx="56" cy="30" rx="1" ry="2.5" fill="#E8C4B8"/>
    {/* Donkey eye */}
    <circle cx="55" cy="35" r="1.5" fill="#1A1A1A"/>
    {/* Donkey nostril */}
    <circle cx="58" cy="38" r="1" fill="#8B7355"/>
    {/* Donkey mouth - smiling */}
    <path d="M56 39 Q59 41 61 39" stroke="#8B7355" strokeWidth="1" fill="none" strokeLinecap="round"/>
    {/* Donkey tail */}
    <path d="M15 40 Q8 38 6 42 Q8 46 12 44" stroke="#A0896B" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <ellipse cx="6" cy="44" rx="2" ry="3" fill="#888" transform="rotate(-20 6 44)"/>
    {/* Donkey legs */}
    <rect x="22" y="50" width="5" height="10" rx="2" fill="#8B7355"/>
    <rect x="30" y="50" width="5" height="10" rx="2" fill="#8B7355"/>
    <rect x="38" y="50" width="5" height="10" rx="2" fill="#8B7355"/>
    <rect x="44" y="50" width="5" height="10" rx="2" fill="#8B7355"/>
    {/* Gulliver on donkey */}
    <ellipse cx="32" cy="32" rx="5" ry="6" fill="#FAFAF8"/>
    <circle cx="32" cy="23" r="6" fill="#F5D5A0"/>
    <circle cx="30" cy="22" r="1" fill="#1A1A1A"/>
    <circle cx="34" cy="22" r="1" fill="#1A1A1A"/>
    <path d="M30 25 Q32 27 34 25" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="32" cy="17" rx="7" ry="2" fill="#C05621"/>
    <rect x="29" y="11" width="6" height="7" rx="1" fill="#C05621"/>
    <rect x="29" y="15" width="6" height="2" fill="#E8B84B"/>
    {/* Arms holding reins */}
    <path d="M27 31 Q22 34 20 36" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M37 31 Q40 30 44 30" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* Reins */}
    <path d="M20 36 Q38 32 52 36" stroke="#8B7355" strokeWidth="1" fill="none" strokeLinecap="round"/>
  </svg>
);

const GulliverOnShip = () => (
  <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Waves */}
    <path d="M0 50 Q10 45 20 50 Q30 55 40 50 Q50 45 60 50 Q70 55 80 50" stroke="#3B6ECC" strokeWidth="2" fill="none"/>
    <path d="M0 55 Q10 50 20 55 Q30 60 40 55 Q50 50 60 55 Q70 60 80 55" stroke="#3B6ECC" strokeWidth="1.5" fill="none" opacity="0.5"/>
    {/* Ship hull */}
    <path d="M10 48 Q12 35 20 33 L60 33 Q68 35 70 48 Z" fill="#8B4513"/>
    <path d="M15 33 L65 33 L62 27 L18 27 Z" fill="#A0522D"/>
    {/* Mast */}
    <line x1="40" y1="5" x2="40" y2="33" stroke="#5D4037" strokeWidth="2.5"/>
    {/* Sails */}
    <path d="M40 8 Q58 15 55 30 L40 30 Z" fill="#FAFAF8" stroke="#DDD" strokeWidth="0.5"/>
    <path d="M40 8 Q22 15 25 30 L40 30 Z" fill="#F5F5F5" stroke="#DDD" strokeWidth="0.5"/>
    {/* Flag */}
    <path d="M40 5 L52 8 L40 11 Z" fill="#C05621"/>
    {/* Portholes */}
    <circle cx="25" cy="40" r="3" fill="#87CEEB" stroke="#5D4037" strokeWidth="1"/>
    <circle cx="55" cy="40" r="3" fill="#87CEEB" stroke="#5D4037" strokeWidth="1"/>
    {/* Gulliver at helm */}
    <ellipse cx="40" cy="26" rx="4" ry="5" fill="#FAFAF8"/>
    <circle cx="40" cy="18" r="5" fill="#F5D5A0"/>
    <circle cx="38" cy="17" r="1" fill="#1A1A1A"/>
    <circle cx="42" cy="17" r="1" fill="#1A1A1A"/>
    <path d="M38 20 Q40 22 42 20" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="40" cy="13" rx="6" ry="2" fill="#C05621"/>
    <rect x="37" y="7" width="6" height="7" rx="1" fill="#C05621"/>
    <rect x="37" y="11" width="6" height="2" fill="#E8B84B"/>
    <path d="M36 25 Q30 24 28 26" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M44 25 Q50 24 52 26" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
);

const GulliverOnBike = () => (
  <svg viewBox="0 0 70 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Back wheel */}
    <circle cx="18" cy="46" r="12" stroke="#1A1A1A" strokeWidth="2.5" fill="none"/>
    <circle cx="18" cy="46" r="2" fill="#1A1A1A"/>
    {/* Front wheel */}
    <circle cx="52" cy="46" r="12" stroke="#1A1A1A" strokeWidth="2.5" fill="none"/>
    <circle cx="52" cy="46" r="2" fill="#1A1A1A"/>
    {/* Spokes back */}
    <line x1="18" y1="34" x2="18" y2="58" stroke="#888" strokeWidth="1"/>
    <line x1="6" y1="46" x2="30" y2="46" stroke="#888" strokeWidth="1"/>
    <line x1="9" y1="37" x2="27" y2="55" stroke="#888" strokeWidth="1"/>
    <line x1="27" y1="37" x2="9" y2="55" stroke="#888" strokeWidth="1"/>
    {/* Spokes front */}
    <line x1="52" y1="34" x2="52" y2="58" stroke="#888" strokeWidth="1"/>
    <line x1="40" y1="46" x2="64" y2="46" stroke="#888" strokeWidth="1"/>
    <line x1="43" y1="37" x2="61" y2="55" stroke="#888" strokeWidth="1"/>
    <line x1="61" y1="37" x2="43" y2="55" stroke="#888" strokeWidth="1"/>
    {/* Frame */}
    <line x1="18" y1="46" x2="35" y2="28" stroke="#C05621" strokeWidth="2.5"/>
    <line x1="35" y1="28" x2="52" y2="46" stroke="#C05621" strokeWidth="2.5"/>
    <line x1="35" y1="28" x2="28" y2="46" stroke="#C05621" strokeWidth="2.5"/>
    {/* Handlebars */}
    <line x1="52" y1="32" x2="52" y2="25" stroke="#1A1A1A" strokeWidth="2"/>
    <line x1="48" y1="25" x2="56" y2="25" stroke="#1A1A1A" strokeWidth="2"/>
    {/* Seat */}
    <line x1="35" y1="28" x2="35" y2="22" stroke="#1A1A1A" strokeWidth="2"/>
    <rect x="31" y="20" width="8" height="3" rx="1.5" fill="#1A1A1A"/>
    {/* Pedals */}
    <circle cx="30" cy="42" r="3" fill="#888"/>
    <line x1="27" y1="44" x2="33" y2="40" stroke="#1A1A1A" strokeWidth="1.5"/>
    {/* Gulliver */}
    <ellipse cx="37" cy="16" rx="5" ry="6" fill="#FAFAF8"/>
    <circle cx="37" cy="8" r="5.5" fill="#F5D5A0"/>
    <circle cx="35" cy="7" r="1" fill="#1A1A1A"/>
    <circle cx="39" cy="7" r="1" fill="#1A1A1A"/>
    <path d="M35 10 Q37 12 39 10" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="37" cy="3" rx="6" ry="2" fill="#C05621"/>
    <rect x="34" y="-3" width="6" height="7" rx="1" fill="#C05621"/>
    <rect x="34" y="1" width="6" height="2" fill="#E8B84B"/>
    {/* Arms on handlebars */}
    <path d="M42 18 Q48 20 52 24" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M32 18 Q28 22 30 28" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* Speed lines */}
    <line x1="0" y1="30" x2="12" y2="30" stroke="#C05621" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="0" y1="36" x2="10" y2="36" stroke="#C05621" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
    <line x1="0" y1="42" x2="8" y2="42" stroke="#C05621" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
  </svg>
);

const GulliverInBalloon = () => (
  <svg viewBox="0 0 60 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Balloon */}
    <ellipse cx="30" cy="22" rx="20" ry="22" fill="#C05621"/>
    {/* Balloon panels */}
    <path d="M30 0 Q38 8 38 22 Q38 36 30 44 Q22 36 22 22 Q22 8 30 0 Z" fill="#E8B84B" opacity="0.6"/>
    <path d="M10 18 Q18 8 30 0 Q22 8 22 22 Q16 28 10 18 Z" fill="#FAFAF8" opacity="0.3"/>
    {/* Balloon tie */}
    <ellipse cx="30" cy="44" rx="4" ry="2" fill="#8B4513"/>
    {/* Ropes */}
    <line x1="26" y1="44" x2="22" y2="54" stroke="#8B4513" strokeWidth="1"/>
    <line x1="34" y1="44" x2="38" y2="54" stroke="#8B4513" strokeWidth="1"/>
    <line x1="28" y1="45" x2="26" y2="54" stroke="#8B4513" strokeWidth="1"/>
    <line x1="32" y1="45" x2="34" y2="54" stroke="#8B4513" strokeWidth="1"/>
    {/* Basket */}
    <rect x="20" y="54" width="20" height="12" rx="2" fill="#A0522D"/>
    <line x1="24" y1="54" x2="24" y2="66" stroke="#8B4513" strokeWidth="1"/>
    <line x1="30" y1="54" x2="30" y2="66" stroke="#8B4513" strokeWidth="1"/>
    <line x1="36" y1="54" x2="36" y2="66" stroke="#8B4513" strokeWidth="1"/>
    {/* Gulliver in basket */}
    <circle cx="30" cy="50" r="5" fill="#F5D5A0"/>
    <circle cx="28" cy="49" r="1" fill="#1A1A1A"/>
    <circle cx="32" cy="49" r="1" fill="#1A1A1A"/>
    <path d="M28 52 Q30 54 32 52" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="30" cy="45" rx="6" ry="2" fill="#C05621"/>
    <rect x="27" y="39" width="6" height="7" rx="1" fill="#C05621"/>
    <rect x="27" y="43" width="6" height="2" fill="#E8B84B"/>
    {/* Waving arm */}
    <path d="M35 52 Q42 46 44 42" stroke="#F5D5A0" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <circle cx="44" cy="41" r="2" fill="#F5D5A0"/>
    {/* Clouds */}
    <ellipse cx="8" cy="15" rx="6" ry="4" fill="white" opacity="0.5"/>
    <ellipse cx="13" cy="12" rx="5" ry="3" fill="white" opacity="0.5"/>
    <ellipse cx="52" cy="35" rx="5" ry="3" fill="white" opacity="0.4"/>
    <ellipse cx="57" cy="32" rx="4" ry="3" fill="white" opacity="0.4"/>
  </svg>
);

const GulliverOnCamel = () => (
  <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Ground/sand */}
    <path d="M0 55 Q20 52 40 55 Q60 58 80 55 L80 60 L0 60 Z" fill="#E8C84B" opacity="0.4"/>
    {/* Camel body */}
    <ellipse cx="38" cy="42" rx="22" ry="12" fill="#C8A96E"/>
    {/* Hump */}
    <ellipse cx="34" cy="30" rx="10" ry="8" fill="#C8A96E"/>
    {/* Neck */}
    <path d="M52 36 Q58 30 56 22 Q54 16 50 14" stroke="#C8A96E" strokeWidth="10" fill="none" strokeLinecap="round"/>
    {/* Head */}
    <ellipse cx="50" cy="12" rx="8" ry="6" fill="#C8A96E"/>
    {/* Ear */}
    <ellipse cx="46" cy="7" rx="2" ry="3" fill="#C8A96E"/>
    {/* Eye */}
    <circle cx="53" cy="10" r="1.5" fill="#1A1A1A"/>
    {/* Nostril */}
    <circle cx="56" cy="14" r="1" fill="#A08050"/>
    {/* Camel smile */}
    <path d="M54 15 Q57 17 59 15" stroke="#A08050" strokeWidth="1" fill="none" strokeLinecap="round"/>
    {/* Tail */}
    <path d="M16 42 Q10 40 8 44" stroke="#C8A96E" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <ellipse cx="7" cy="45" rx="2" ry="3" fill="#A08050"/>
    {/* Legs */}
    <rect x="20" y="52" width="5" height="10" rx="2" fill="#A08050"/>
    <rect x="28" y="52" width="5" height="10" rx="2" fill="#A08050"/>
    <rect x="40" y="52" width="5" height="10" rx="2" fill="#A08050"/>
    <rect x="48" y="52" width="5" height="10" rx="2" fill="#A08050"/>
    {/* Gulliver on hump */}
    <ellipse cx="32" cy="22" rx="5" ry="6" fill="#FAFAF8"/>
    <circle cx="32" cy="13" r="6" fill="#F5D5A0"/>
    <circle cx="30" cy="12" r="1" fill="#1A1A1A"/>
    <circle cx="34" cy="12" r="1" fill="#1A1A1A"/>
    <path d="M30 15 Q32 17 34 15" stroke="#1A1A1A" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
    <ellipse cx="32" cy="8" rx="6" ry="2" fill="#C05621"/>
    <rect x="29" y="2" width="6" height="7" rx="1" fill="#C05621"/>
    <rect x="29" y="6" width="6" height="2" fill="#E8B84B"/>
    {/* Arms */}
    <path d="M27 24 Q22 22 20 24" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M37 22 Q42 18 44 16" stroke="#FAFAF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <circle cx="44" cy="15" r="2" fill="#F5D5A0"/>
    {/* Sun */}
    <circle cx="68" cy="8" r="6" fill="#E8C84B"/>
    <line x1="68" y1="0" x2="68" y2="2" stroke="#E8C84B" strokeWidth="1.5"/>
    <line x1="76" y1="8" x2="78" y2="8" stroke="#E8C84B" strokeWidth="1.5"/>
    <line x1="74" y1="2" x2="75" y2="0" stroke="#E8C84B" strokeWidth="1.5"/>
    <line x1="74" y1="14" x2="75" y2="16" stroke="#E8C84B" strokeWidth="1.5"/>
  </svg>
);

// ---- Scene configs ----
const SCENES = [
  {
    id: "plane",
    label: "✈️ Flying",
    component: GulliverOnPlane,
    animation: "orbit",
    bgText: null,
  },
  {
    id: "train",
    label: "🚆 By Train",
    component: GulliverOnTrain,
    animation: "chug",
    bgText: null,
  },
  {
    id: "donkey",
    label: "🫏 On Donkey",
    component: GulliverOnDonkey,
    animation: "trot",
    bgText: null,
  },
  {
    id: "ship",
    label: "🚢 By Sea",
    component: GulliverOnShip,
    animation: "sail",
    bgText: null,
  },
  {
    id: "bike",
    label: "🚲 Cycling",
    component: GulliverOnBike,
    animation: "pedal",
    bgText: null,
  },
  {
    id: "balloon",
    label: "🎈 Ballooning",
    component: GulliverInBalloon,
    animation: "float",
    bgText: null,
  },
  {
    id: "camel",
    label: "🐪 By Camel",
    component: GulliverOnCamel,
    animation: "sway",
    bgText: null,
  },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [scene] = useState(() => SCENES[Math.floor(Math.random() * SCENES.length)]);
  const [page, setPage] = useState("trips");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [restaurantAutoLocal, setRestaurantAutoLocal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isOnline, setIsOnline] = useState(true); // assume online until proven otherwise
  const savedScrollY = useRef(0);

  // Show splash briefly (1.2s) every time app opens
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // If we're still on the splash after 2.5s (slow or offline), switch to the full animation
  const [slowLoad, setSlowLoad] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlowLoad(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  // Safety timeout — if Firebase auth hasn't resolved in 35s (e.g. no connection
  // and slow to fall back to offline mode), clear the loading state anyway so the
  // app doesn't hang on the splash screen
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 35000);
    return () => clearTimeout(timer);
  }, []);

  const fixSharing = async (u) => {
    try {
      // Check trips where this user's email is in members[] or pendingMembers[]
      const [byMembers, byPending] = await Promise.all([
        getDocs(query(collection(db, "trips"), where("members", "array-contains", u.email))),
        getDocs(query(collection(db, "trips"), where("pendingMembers", "array-contains", u.email.toLowerCase()))),
      ]);

      // Deduplicate by trip id
      const tripMap = {};
      [...byMembers.docs, ...byPending.docs].forEach(d => { tripMap[d.id] = d; });

      for (const tripDoc of Object.values(tripMap)) {
        const data = tripDoc.data();
        const updates = {};
        // Add uid to memberIds if missing
        if (!data.memberIds?.includes(u.uid)) {
          updates.memberIds = arrayUnion(u.uid);
        }
        // Add email to members if somehow missing
        if (!data.members?.includes(u.email)) {
          updates.members = arrayUnion(u.email);
        }
        // Remove from pendingMembers now that uid is resolved
        if (data.pendingMembers?.includes(u.email.toLowerCase())) {
          // arrayRemove isn't imported — use a filter approach via set
          updates.pendingMembers = (data.pendingMembers || []).filter(
            e => e !== u.email.toLowerCase()
          );
        }
        if (Object.keys(updates).length > 0) {
          await updateDoc(doc(db, "trips", tripDoc.id), updates);
        }
      }
    } catch (e) {
      console.error("Fix sharing error:", e);
    }
  };

  // Deep link: opening /?trip=TRIP_ID (e.g. from a calendar event) jumps straight to that trip
  const openTripFromUrl = async (u) => {
    try {
      const tripId = new URLSearchParams(window.location.search).get("trip");
      if (!tripId) return;
      const snap = await getDoc(doc(db, "trips", tripId));
      if (snap.exists() && snap.data().memberIds?.includes(u.uid)) {
        setSelectedTrip({ id: snap.id, ...snap.data() });
        setPage("trip");
      }
      // Clean the URL so a refresh doesn't re-trigger the jump
      window.history.replaceState(null, "", window.location.pathname);
    } catch (e) {
      console.warn("Open trip from URL failed:", e);
    }
  };

  const sendWelcomeEmailIfNew = async (u) => {
    try {
      const snap = await getDoc(doc(db, "users", u.uid));
      if (snap.exists() && snap.data().hasSeenWelcome) return;
      await setDoc(doc(db, "users", u.uid), { hasSeenWelcome: true }, { merge: true });
      await fetch("/api/welcome-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: u.email, name: u.displayName }),
      });
    } catch (e) {
      console.warn("Welcome email failed:", e);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setLoading(false);
      if (u) {
        reportLastSeen(u); // fire-and-forget: records "last seen" in the app-activity tracker
        await fixSharing(u);
        await openTripFromUrl(u);
        sendWelcomeEmailIfNew(u); // fire-and-forget
        warmCache(u.uid); // fire-and-forget: pre-warm IndexedDB for offline use
        // Show onboarding cards if not yet seen
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          if (!snap.exists() || !snap.data().hasSeenOnboarding) {
            setShowOnboarding(true);
          }
        } catch (e) {
          console.warn("Onboarding check failed:", e);
        }
      }
    });
    return unsub;
  }, []);

  // Online / offline detection via navigator.onLine + native events
  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Android back button — maintain a single history entry, never stack up
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      if (page !== "trips") {
        setPage("trips");
        setSelectedTrip(null);
        window.history.pushState(null, "", window.location.href);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedScrollY.current);
          });
        });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [page]);

  const handleSelectTrip = (trip) => {
    savedScrollY.current = window.scrollY;
    setSelectedTrip(trip);
    setPage("trip");
  };

  const handleBack = () => {
    setPage("trips");
    setSelectedTrip(null);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, savedScrollY.current);
      });
    });
  };

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setSelectedTrip(null);
    setPage("trips");
  };

  const SceneComponent = scene.component;

  if (showSplash || loading) {
    // Slow to load (e.g. offline): show the full animated scene
    if (slowLoad) {
      return (
        <div className="splash">
          <div className="splash-trail" />
          <div className={`splash-plane splash-${scene.id}`}>
            <SceneComponent />
          </div>
          <div className="splash-logo">Gulliver Travels</div>
          <div className="splash-tagline">{scene.label}</div>
        </div>
      );
    }
    // Normal fast open: continues Android's launch splash — same globe icon, same
    // centred spot, with the title added underneath. Tweak ICON_SIZE if the globe
    // appears to jump in size when the launch splash hands over.
    const ICON_SIZE = 192;
    return (
      <div
        className="splash"
        style={{
          background: "#FAFAF8", // matches manifest background_color
          minHeight: "100vh",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src="/icons/icon-512.png"
          alt=""
          width={ICON_SIZE}
          height={ICON_SIZE}
          draggable={false}
          style={{ display: "block" }}
        />
        <div
          className="splash-logo"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `calc(50% + ${ICON_SIZE / 2 + 16}px)`,
            textAlign: "center",
            color: "#C05621",
            opacity: 1,
            visibility: "visible",
            animation: "none",
            transform: "none",
          }}
        >
          Gulliver Travels
        </div>
      </div>
    );
  }

  if (!user) {
  if (!isOnline) {
    return (
      <div className="signin-screen">
        <div className="signin-card">
          <div className="signin-logo">Gulliver Travels</div>
          <div className="signin-tagline">Loading your trips…</div>
          <div style={{ fontSize: 13, color: "var(--ink-light)", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
            Offline: Opening cached trips. Any changes will be synced when back online.
          </div>
        </div>
      </div>
    );
  }
    return (
      <div className="signin-screen">
        <div className="signin-card">
          <div className="signin-logo">Gulliver Travels</div>
          <div className="signin-tagline">Your travels, beautifully organized</div>
          <button className="signin-btn" onClick={handleSignIn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
          <div className="signin-hint">
            Forward any confirmation to<br/>
            <strong>GulliverTravelsPlans@gmail.com</strong>
          </div>
        </div>
      </div>
    );
  }

  if (page === "tripit") {
    return <div className="app"><TripItImport user={user} onBack={handleBack} onDone={handleBack} /></div>;
  }
  if (page === "trip" && selectedTrip) {
    return <div className="app"><TripDetail trip={selectedTrip} user={user} onBack={handleBack} onSignOut={handleSignOut}
      onLocalRestaurants={() => { setRestaurantAutoLocal(true); setPage("restaurants"); }} /></div>;
  }
  if (page === "stats") {
    return <div className="app"><StatsPage user={user} onBack={handleBack} /></div>;
  }
  if (page === "restaurants") {
    return <div className="app"><RestaurantsPage user={user} onBack={() => { setRestaurantAutoLocal(false); handleBack(); }}
      autoLocalGroup={restaurantAutoLocal} /></div>;
  }
  if (page === "settings") {
    return <div className="app"><SettingsPage user={user} onBack={handleBack} onImport={() => setPage("tripit")} onSignOut={handleSignOut} /></div>;
  }
  if (page === "packing") {
    return <div className="app"><PackingListsPage user={user} onBack={handleBack} /></div>;
  }

  return (
    <div className="app">
      <TripsList
        user={user}
        onSelectTrip={handleSelectTrip}
        onSignOut={handleSignOut}
        onStats={() => setPage("stats")}
        onRestaurants={() => setPage("restaurants")}
        onPacking={() => setPage("packing")}
        onSettings={() => setPage("settings")}
        isOnline={isOnline}
        onImport={() => setPage("tripit")}
        savedScrollY={savedScrollY}
      />
      
      {showOnboarding && (
        <OnboardingCards user={user} onDone={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}